import { useEffect, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import { useMutation } from '@tanstack/react-query';
import { Button, PhoneInput } from '@/shared/components/ui';
import { userApi } from '@/features/users/api/user.api';
import { useAuth } from '@/features/auth/hooks/useAuth';
import type { User } from '@/types';
import {
  isGoogleAuthWithEmail,
  isValidEmail,
  resolveCheckoutEmail,
} from '@/features/booking/utils/paymentEmail';
import {
  fromE164India,
  isValidIndianMobile,
  toE164India,
} from '@/shared/utils/phone';

interface TravelerContactDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirmed?: (user: User) => void;
  title?: string;
  description?: string;
  requireEmail?: boolean;
}

function defaultDisplayName(user: User | null) {
  if (!user) return '';
  if (user.displayName) return user.displayName;
  const full = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
  return full;
}

export function TravelerContactDialog({
  open,
  onClose,
  onConfirmed,
  title = 'Confirm your contact details',
  description = 'Your guide will use these details to reach you about your booking.',
  requireEmail = false,
}: TravelerContactDialogProps) {
  const { user, updateUser } = useAuth();
  const [displayName, setDisplayName] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const googleEmailLocked = isGoogleAuthWithEmail(user);
  const showEmailField = requireEmail || !user?.email;

  useEffect(() => {
    if (!open || !user) return;
    setDisplayName(defaultDisplayName(user));
    setContactPhone(fromE164India(user.contactPhone ?? user.phone));
    setContactEmail(resolveCheckoutEmail(user));
    setError(null);
  }, [open, user]);

  const saveMutation = useMutation({
    mutationFn: () =>
      userApi.updateContact({
        displayName: displayName.trim(),
        contactPhone: toE164India(contactPhone),
        email: googleEmailLocked ? undefined : contactEmail.trim(),
      }),
    onSuccess: (updatedUser) => {
      updateUser(updatedUser);
      onConfirmed?.(updatedUser);
      onClose();
    },
    onError: (err: Error) => {
      setError(err.message ?? 'Failed to save contact details');
    },
  });

  const handleSubmit = () => {
    if (displayName.trim().length < 2) {
      setError('Please enter your name');
      return;
    }
    if (!isValidIndianMobile(contactPhone)) {
      setError('Please enter a valid 10-digit mobile number');
      return;
    }
    if (showEmailField && !isValidEmail(contactEmail)) {
      setError('Please enter a valid email address');
      return;
    }
    setError(null);
    saveMutation.mutate();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      PaperProps={{
        sx: {
          borderRadius: (t) => t.radii.lg,
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
        },
      }}
    >
      <DialogTitle sx={{ fontWeight: 700, color: 'textColors.heading' }}>{title}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {description}
        </Typography>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <TextField
          label="Your name"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          fullWidth
          margin="normal"
          autoFocus
        />
        <PhoneInput
          label="Contact phone"
          value={contactPhone}
          onChange={setContactPhone}
          fullWidth
          margin="normal"
        />
        {showEmailField && (
          <TextField
            label="Email for payment receipts"
            type="email"
            value={contactEmail}
            onChange={(e) => setContactEmail(e.target.value)}
            fullWidth
            margin="normal"
            required
            InputProps={{ readOnly: googleEmailLocked }}
            helperText={
              googleEmailLocked
                ? 'From your Google account'
                : 'Required for secure payment via Cashfree'
            }
          />
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button intent="ghost" onClick={onClose} disabled={saveMutation.isPending}>
          Cancel
        </Button>
        <Button intent="primary-dark" onClick={handleSubmit} disabled={saveMutation.isPending}>
          {saveMutation.isPending ? 'Saving…' : 'Confirm'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function needsContactConfirmation(user: User | null) {
  return Boolean(user && !user.contactConfirmedAt);
}
